import { useContext, useEffect, useRef, useState } from 'react';
import { collection, doc, onSnapshot, query, updateDoc, where, limit } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { UserContext } from './UserContext';
import { getPedalsFromUsersWhoWantMine, enrichPedalsWithOwnerData } from '../utils/recommendations';
import FirstMatchModal from '../components/FirstMatchModal';

/**
 * FirstMatchProvider — mounts once at app level (inside UserProvider +
 * BrowserRouter). Watches the current user's wantsMyPedals collection and
 * shows FirstMatchModal exactly once: the first time ≥1 match doc appears,
 * after the Onboarding Quickstart has been dismissed.
 *
 * State-tracking strategy:
 *   Primary:   users/{uid}.firstMatchModalShown (Firestore, cross-device)
 *   Secondary: localStorage `firstMatch_shown_{uid}` (instant reads, avoids
 *              re-show while the Firestore write propagates back)
 *
 * Deference rule: the listener only starts once firestoreUser.onboardingModalSeen
 * is true. Matches that appeared while the Quickstart was still open will be
 * in Firestore already; the snapshot fires immediately on the first read.
 */
const FirstMatchProvider = ({ children }) => {
  const { firestoreUser } = useContext(UserContext);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [matchPedals, setMatchPedals] = useState([]);
  const unsubRef = useRef(null);

  const markShown = async (uid) => {
    localStorage.setItem(`firstMatch_shown_${uid}`, 'true');
    try {
      await updateDoc(doc(db, 'users', uid), { firstMatchModalShown: true });
    } catch (e) {
      console.error('[FirstMatch] Failed to persist shown state:', e);
    }
  };

  const handleClose = async () => {
    const uid = auth.currentUser?.uid;
    if (uid) await markShown(uid);
    setIsModalOpen(false);
  };

  useEffect(() => {
    // Tear down any leftover listener before re-evaluating
    if (unsubRef.current) {
      unsubRef.current();
      unsubRef.current = null;
    }

    const uid = auth.currentUser?.uid;

    // Not logged in or user doc not yet loaded
    if (!uid || !firestoreUser) return;

    // Already shown (Firestore source of truth OR localStorage fast-path)
    if (
      firestoreUser.firstMatchModalShown === true ||
      localStorage.getItem(`firstMatch_shown_${uid}`) === 'true'
    ) return;

    // Only eligible for users who have successfully listed their first pedal.
    // AddPedalForm writes this flag after a confirmed addDoc — existing accounts
    // and failed submissions never have it, so the modal never fires for them.
    if (!firestoreUser.firstMatchEligible) return;

    // Defer until the Onboarding Quickstart has been dismissed.
    // When onboardingModalSeen flips to true this effect re-runs,
    // and the snapshot fires immediately if matches already exist.
    if (!firestoreUser.onboardingModalSeen) return;

    // Start the wantsMyPedals listener — limit(3) because we only need
    // to know that ≥1 doc exists, not the full set.
    const q = query(
      collection(db, 'wantsMyPedals'),
      where('pedalOwnerId', '==', uid),
      limit(3)
    );

    unsubRef.current = onSnapshot(
      q,
      async (snap) => {
        if (snap.empty) return;

        // Unsubscribe immediately — this is a one-shot trigger.
        if (unsubRef.current) {
          unsubRef.current();
          unsubRef.current = null;
        }

        // Fetch up to 3 fully-enriched cards before marking shown.
        // If enrichment fails or returns empty we do NOT mark shown and do NOT
        // open the modal — the provider will retry on next login when the
        // listener restarts and the snapshot fires again immediately.
        let enriched = [];
        try {
          const pedals = await getPedalsFromUsersWhoWantMine(uid, 3);
          enriched = await enrichPedalsWithOwnerData(pedals);
        } catch (e) {
          console.error('[FirstMatch] Failed to load card data:', e);
        }

        if (enriched.length === 0) return;

        // Cards confirmed — mark shown then open.
        await markShown(uid);
        setMatchPedals(enriched);
        setIsModalOpen(true);
      },
      (err) => {
        console.error('[FirstMatch] Snapshot error:', err);
      }
    );

    return () => {
      if (unsubRef.current) {
        unsubRef.current();
        unsubRef.current = null;
      }
    };
  }, [
    // Depend only on the scalar fields that gate this feature, not the whole
    // firestoreUser object. The full object is a new reference on every
    // UserContext snapshot, which would churn the listener on every unrelated
    // Firestore update (trades, notifications, etc.).
    !!firestoreUser,                          // login / logout
    firestoreUser?.firstMatchEligible,        // flips after first listing
    firestoreUser?.onboardingModalSeen,       // flips after Quickstart dismissed
    firestoreUser?.firstMatchModalShown,      // flips after modal shown
  ]);

  return (
    <>
      {children}
      <FirstMatchModal
        isOpen={isModalOpen}
        pedals={matchPedals}
        onClose={handleClose}
      />
    </>
  );
};

export default FirstMatchProvider;
